import React, { useState, useEffect } from 'react';
import {
  Clock, AlertCircle, CheckCircle, TrendingUp, Calendar, User,
  Building, Filter, Bug, Wrench, ChevronDown, ChevronUp,
  Circle, ArrowUp, ArrowDown, Loader2, RefreshCw, Search
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import { motion } from 'framer-motion';
import api from '../../api';

const TableauBord = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [utilisateurId, setUtilisateurId] = useState(null);
    useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setUtilisateurId(parseInt(user.id, 10));
      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Récupération des données depuis l'API
  const fetchTickets = async () => {
    try {
      setLoading(true);
      setIsRefreshing(true);
      setError(null);

      console.log("ID utilisateur récupéré:", utilisateurId);

      if (!utilisateurId) {
        throw new Error("ID utilisateur non trouvé ");
      }

      // 🔹 Requête Axios
      const { data } = await api.get(`/tickets/developpeur/${utilisateurId}`);

      // Vérification des données
      if (data.tickets && Array.isArray(data.tickets)) {
        setTickets(data.tickets);
      } else {
        setTickets([]);
      }

    } catch (err) {
      setError(err.message || "Erreur lors du chargement des tickets");
      console.error("Erreur lors du chargement des tickets:", err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const priorityOptions = [
    { value: '1', label: 'Faible' },
    { value: '2', label: 'Moyenne' },
    { value: '3', label: 'Haute' },
  ];

  // Filtrage des tickets
  const filteredTickets = tickets.filter(ticket => {
    const priorityMatch = selectedPriority === 'all' || ticket.priorite.toString() === selectedPriority;
    const searchMatch = ticket.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.id.toString().includes(searchTerm);
    return priorityMatch && searchMatch;
  });

  // Tri des tickets
  const sortedTickets = React.useMemo(() => {
    let sortableItems = [...filteredTickets];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredTickets, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Calcul des métriques
  const getMetrics = () => {
    const totalTickets = filteredTickets.length;
    const ticketsEnCours = filteredTickets.filter(t =>
      !((t.etape_id === 3 && t.status === 3) || t.etape_id === 6 || t.etape_id === 7)
    ).length;
    const ticketsTermines = filteredTickets.filter(t =>
      (t.etape_id === 3 && t.status === 3) || t.etape_id === 6 || t.etape_id === 7
    ).length;
    const ticketsBugs = filteredTickets.filter(t => t.type === 'bug').length;
    const ticketsDemandes = filteredTickets.filter(t => t.type === 'demande').length;

    return {
      totalTickets,
      ticketsEnCours,
      ticketsTermines,
      ticketsBugs,
      ticketsDemandes,
      tauxResolution: totalTickets > 0 ? ((ticketsTermines / totalTickets) * 100).toFixed(1) : 0
    };
  };

  // Données pour les graphiques
  const getEvolutionData = () => {
    const ticketsByDate = {};

    filteredTickets.forEach(ticket => {
      const date = new Date(ticket.created_at).toLocaleDateString('fr-FR');
      ticketsByDate[date] = (ticketsByDate[date] || 0) + 1;
    });

    return Object.entries(ticketsByDate)
      .sort(([a], [b]) => new Date(a.split('/').reverse().join('-')) - new Date(b.split('/').reverse().join('-')))
      .map(([date, count]) => ({ date, tickets: count }));
  };

  const getTypeData = () => {
    const typeCount = {};
    filteredTickets.forEach(ticket => {
      typeCount[ticket.type] = (typeCount[ticket.type] || 0) + 1;
    });

    return Object.entries(typeCount).map(([type, count]) => ({
      name: type === 'bug' ? 'Bugs' : 'Demandes',
      value: count
    }));
  };

  const getPriorityData = () => {
    const priorityCount = {};
    filteredTickets.forEach(ticket => {
      const priority = ticket.priorite === '1' ? 'Basse' : ticket.priorite === '2' ? 'Moyenne' : 'Haute';
      priorityCount[priority] = (priorityCount[priority] || 0) + 1;
    });

    return Object.entries(priorityCount).map(([priority, count]) => ({
      priority,
      count
    }));
  };

  // Fonctions utilitaires
  const getPriorityColor = (priorite) => {
    switch (priorite.toString()) {
      case '1': return 'bg-green-100 text-green-800';
      case '2': return 'bg-yellow-100 text-yellow-800';
      case '3': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityText = (priorite) => {
    switch (priorite) {
      case '1': return 'Basse';
      case '2': return 'Moyenne';
      case '3': return 'Haute';
      default: return 'Non définie';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case '1': return 'En cours';
      case '2': return 'En test';
      case '3': return 'Terminé';
      default: return 'Non défini';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case '1': return 'bg-blue-100 text-blue-800';
      case '2': return 'bg-purple-100 text-purple-800';
      case '3': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const metrics = getMetrics();
  const evolutionData = getEvolutionData();
  const typeData = getTypeData();
  const priorityData = getPriorityData();

  const COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'];

  if (loading && !isRefreshing) {
    return (
      <div className="flex justify-center items-center h-screen">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Loader2 className="h-12 w-12 text-blue-600" />
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex justify-center items-center h-screen"
      >
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur de chargement</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchTickets}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center mx-auto"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Réessayer
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen bg-gray-50 p-4 md:p-6"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div variants={itemVariants} className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Tableau de Bord Technique
              </h1>
              <p className="text-gray-600">
                Vue d'ensemble de vos tickets assignés
              </p>
            </div>
            <button
              onClick={fetchTickets}
              disabled={isRefreshing}
              className={`px-4 py-2 rounded-md flex items-center gap-2 ${isRefreshing ? 'bg-gray-200' : 'bg-white border border-gray-300 hover:bg-gray-50'} transition-colors`}
            >
              {isRefreshing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Chargement...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span>Actualiser</span>
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* Filtres et recherche */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Rechercher un ticket..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-2">
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                <Filter className="h-5 w-5 text-gray-500" />
                <select
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value)}
                  className="bg-transparent border-none text-sm focus:ring-0 focus:outline-none"
                >
                  <option value="all">Toutes priorités</option>
                  <option value="3">Haute</option>
                  <option value="2">Moyenne</option>
                  <option value="1">Basse</option>
                </select>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Métriques principales */}
        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
        >
          <motion.div variants={itemVariants} whileHover={{ y: -5 }} className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Tickets</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.totalTickets}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {metrics.ticketsBugs} bugs • {metrics.ticketsDemandes} demandes
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <Wrench className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} whileHover={{ y: -5 }} className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En Cours</p>
                <p className="text-2xl font-bold text-yellow-600">{metrics.ticketsEnCours}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {metrics.totalTickets > 0 ? Math.round((metrics.ticketsEnCours / metrics.totalTickets) * 100) : 0}% du total
                </p>
              </div>
              <div className="h-12 w-12 bg-yellow-50 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} whileHover={{ y: -5 }} className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Terminés</p>
                <p className="text-2xl font-bold text-green-600">{metrics.ticketsTermines}</p>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="text-green-600 font-medium">+{metrics.tauxResolution}%</span> taux de résolution
                </p>
              </div>
              <div className="h-12 w-12 bg-green-50 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} whileHover={{ y: -5 }} className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Évolution</p>
                <p className="text-2xl font-bold text-purple-600">
                  {evolutionData.length > 0 ? evolutionData[evolutionData.length - 1].tickets : 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {evolutionData.length > 1 && (
                    <>
                      {evolutionData[evolutionData.length - 1].tickets > evolutionData[0].tickets ? (
                        <span className="text-green-600 flex items-center">
                          <ArrowUp className="h-3 w-3 mr-1" />
                          {((evolutionData[evolutionData.length - 1].tickets / evolutionData[0].tickets - 1) * 100).toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-red-600 flex items-center">
                          <ArrowDown className="h-3 w-3 mr-1" />
                          {((1 - evolutionData[evolutionData.length - 1].tickets / evolutionData[0].tickets) * 100).toFixed(1)}%
                        </span>
                      )}
                      vs début
                    </>
                  )}
                </p>
              </div>
              <div className="h-12 w-12 bg-purple-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Graphiques */}
        <motion.div variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Évolution des tickets */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Évolution des Tickets</h3>
              <div className="flex gap-2">
                <Circle className="h-4 w-4 text-[#8884d8]" />
                <span className="text-sm text-gray-600">Nouveaux tickets</span>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolutionData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickMargin={10}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickMargin={10}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="tickets"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Répartition par type */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Type</h3>
            <div className="h-64 flex flex-col">
              <div className="flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={typeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {typeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value} tickets`, 'Quantité']}
                      contentStyle={{
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend
                      iconType="circle"
                      formatter={(value) => value === 'Bugs' ? 'Bugs' : 'Demandes'}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-2">
                {typeData.map((entry, index) => (
                  <div key={entry.name} className="flex items-center">
                    <div
                      className="h-3 w-3 rounded-full mr-1"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-xs text-gray-600">
                      {entry.name}: {entry.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Graphique de priorité */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Priorité</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={priorityData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis
                  dataKey="priority"
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar
                  dataKey="count"
                  radius={[4, 4, 0, 0]}
                >
                  {priorityData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry.priority === 'Haute' ? '#FF6384' :
                          entry.priority === 'Moyenne' ? '#FFCE56' : '#36A2EB'
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Liste des tickets */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Tickets Assignés ({filteredTickets.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-1"
                      onClick={() => requestSort('id')}
                    >
                      Ticket
                      {sortConfig.key === 'id' && (
                        sortConfig.direction === 'ascending' ?
                          <ChevronUp className="h-4 w-4" /> :
                          <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-1"
                      onClick={() => requestSort('priorite')}
                    >
                      Priorité
                      {sortConfig.key === 'priorite' && (
                        sortConfig.direction === 'ascending' ?
                          <ChevronUp className="h-4 w-4" /> :
                          <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organisation
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-1"
                      onClick={() => requestSort('date_livraison')}
                    >
                      Date Livraison
                      {sortConfig.key === 'date_livraison' && (
                        sortConfig.direction === 'ascending' ?
                          <ChevronUp className="h-4 w-4" /> :
                          <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedTickets.map((ticket) => (
                  <motion.tr
                    key={ticket.id}
                    className="hover:bg-gray-50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center">
                          {ticket.type === 'bug' ? (
                            <Bug className="h-5 w-5 text-red-500" />
                          ) : (
                            <Wrench className="h-5 w-5 text-blue-500" />
                          )}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            #{ticket.id}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {ticket.nom}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ticket.type === 'bug' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                        {ticket.type === 'bug' ? 'Bug' : 'Demande'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priorite)}`}>
                        {getPriorityText(ticket.priorite)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                        {getStatusText(ticket.status)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Building className="h-4 w-4 text-gray-400 mr-2" />
                        {ticket.organisation?.nomOrganisation || 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        {new Date(ticket.date_livraison).toLocaleDateString('fr-FR')}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          {sortedTickets.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <Wrench className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucun ticket trouvé</p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default TableauBord;